"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, ChevronLeft } from "lucide-react";
import Link from "next/link";

const MIN_SCREENS = 1;
const MAX_SCREENS = 12;
const DEFAULT_SCREENS = 4;
// Illustrative assumption only (labeled as an example on the page) — not any specific vendor's price.
const EXAMPLE_PER_SCREEN = 40; // $/screen/month

function useOdometer(target: number, durationMs = 700) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = display;
    const delta = target - start;
    const startedAt = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + delta * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return display;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export default function KitchenDisplayHero() {
  const { t } = useLang();
  const [screens, setScreens] = useState(DEFAULT_SCREENS);
  const addOnMonthly = screens * EXAMPLE_PER_SCREEN;
  const yearlySavings = addOnMonthly * 12;

  const addOnDisplay = useOdometer(addOnMonthly);
  const yearlyDisplay = useOdometer(yearlySavings);
  const settleControls = useAnimation();

  const handleRelease = () => {
    settleControls.start({
      scale: [1, 1.04, 1],
      transition: { duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] },
    });
  };

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#FAFAF8]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-kitchen-display.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAF8]/80 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Multi-station KDS · In the box", "KDS multi-estación · En la caja")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("In the box.", "En la caja.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Every screen.", "Cada pantalla.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "Many POS systems charge a monthly fee for every kitchen screen. Redy includes unlimited stations, in every plan.",
            "Muchos POS cobran una cuota mensual por cada pantalla de cocina. Redy incluye estaciones ilimitadas, en cada plan."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[920px] mx-auto"
        >
          <div
            className="absolute -inset-4 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(244,114,43,0.12) 0%, transparent 65%)" }}
          />

          <motion.div
            animate={settleControls}
            className="relative bg-white rounded-[32px] border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.06)] p-8 md:p-12"
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
              {t("Kitchen screens in your line", "Pantallas de cocina en tu línea")}
            </div>

            <div className="flex items-end justify-between mb-3">
              <div className="text-[42px] md:text-[56px] font-black tracking-tight text-redy-charcoal leading-none tabular-nums">
                {screens}
              </div>
              <div className="text-[12px] font-bold text-redy-secondary mb-2">
                {screens === 1
                  ? t("station", "estación")
                  : t("stations", "estaciones")}{" "}
                · {t("grill / fryer / salad / expo", "grill / freidora / salad / expo")}
              </div>
            </div>

            <input
              type="range"
              min={MIN_SCREENS}
              max={MAX_SCREENS}
              step={1}
              value={screens}
              onChange={(e) => setScreens(Number(e.target.value))}
              onMouseUp={handleRelease}
              onTouchEnd={handleRelease}
              className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#F4722B] to-[#D7263D]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F4722B] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
            />

            <div className="flex justify-between text-[11px] font-bold text-redy-secondary/50 mt-2">
              <span>{MIN_SCREENS}</span>
              <span>{MAX_SCREENS}+</span>
            </div>

            <div className="mt-4 text-[12px] font-medium text-redy-secondary/70">
              {t("That's", "Eso es")}{" "}
              <span className="font-bold text-redy-charcoal tabular-nums">
                {screens} {screens === 1 ? t("iPad", "iPad") : t("iPads", "iPads")}
              </span>{" "}
              {t(
                "or browsers running your kitchen, all the time.",
                "o browsers corriendo tu cocina, todo el tiempo."
              )}
            </div>

            <div className="h-px bg-black/5 my-10" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
              <div className="text-left">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
                  {t("A per-screen add-on (example)", "Un add-on por pantalla (ejemplo)")}
                </div>
                <div className="text-[40px] md:text-[52px] font-black tracking-tight text-redy-charcoal/40 leading-none tabular-nums">
                  −{formatCurrency(addOnDisplay)}
                </div>
                <div className="text-[12px] font-medium text-redy-secondary/70 mt-2">
                  {t(
                    `Example: ${EXAMPLE_PER_SCREEN} dollars per screen, every month.`,
                    `Ejemplo: ${EXAMPLE_PER_SCREEN} dólares por pantalla, cada mes.`
                  )}
                </div>
              </div>

              <div className="text-left relative">
                <div
                  className="absolute -inset-x-4 -inset-y-3 rounded-[20px] pointer-events-none"
                  style={{ background: "radial-gradient(ellipse, rgba(244,114,43,0.08) 0%, transparent 70%)" }}
                />
                <div className="relative">
                  <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#D7263D" }}>
                    {t("Redy charges", "Redy cobra")}
                  </div>
                  <div className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums" style={{ color: "#D7263D" }}>
                    $0
                  </div>
                  <div className="text-[12px] font-medium mt-2" style={{ color: "#D7263D", opacity: 0.7 }}>
                    {t("Unlimited stations. Every plan.", "Estaciones ilimitadas. Cada plan.")}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#F4722B] to-[#D7263D] rounded-[24px] px-6 py-6 md:px-8 md:py-8 text-white shadow-lg shadow-[#F4722B]/20">
              <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                <div>
                  <div className="text-[11px] font-black tracking-widest uppercase text-white/70 mb-2">
                    {t("Stays in your bank", "Se queda en tu banco")}
                  </div>
                  <div className="text-[48px] md:text-[64px] font-black leading-none tabular-nums">
                    +{formatCurrency(yearlyDisplay)}
                  </div>
                  <div className="text-[13px] font-bold text-white/85 mt-1">
                    /{t("year", "año")} · {formatCurrency(addOnMonthly)}/{t("month", "mes")}
                  </div>
                </div>
                <Link
                  href="/signup"
                  className="inline-flex items-center gap-2 bg-white text-redy-charcoal font-bold text-[14px] px-6 py-3.5 rounded-full hover:scale-[1.03] transition-transform shadow-lg whitespace-nowrap"
                >
                  {t("Add screens for free", "Agrega pantallas gratis")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8 text-[12px] font-medium text-redy-secondary/60"
        >
          {t(
            "Example only: assumes a kitchen-display add-on at $40 per screen per month. Add-on pricing varies by POS and plan.",
            "Solo un ejemplo: supone un add-on de pantalla de cocina a $40 por pantalla al mes. El precio de los add-ons varía según el POS y el plan."
          )}
        </motion.div>
      </div>
    </section>
  );
}
